import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { i18n } from '@lingui/core'
import styled, { useTheme } from 'styled-components'

import { FlexColumn, FlexRow } from '@/@bothrs-ui/web/components/layout'
import { globalDefinitions } from '@/defined-translations'
import { theme } from '@/theme-generated'
import { TaskStatusLabel } from './TaskStatusLabel'

import type { Task } from '../../types/task.types'

function TaskIcon({ task }: { task: Task }) {
  switch (task.type) {
    case 'Message': {
      return <FontAwesomeIcon icon={['fal', 'file-check']} color={theme.Colors.Neutrals.Darkest} />
    }
    case 'Document': {
      return <FontAwesomeIcon icon={['fal', 'signature']} color={theme.Colors.Neutrals.Darkest} />
    }
    case 'Remecare': {
      return <img src='/img/svg/remecare-icon.svg' alt='Remecare' />
    }
    case 'Hylyght': {
      return <img src='/img/svg/hylyght-icon.svg' alt='Hylyght' />
    }
    case 'Content': {
      return <FontAwesomeIcon icon={['fal', 'newspaper']} color={theme.Colors.Neutrals.Darkest} />
    }
    case 'Questionnaire': {
      if (task.questionnaireResponse?.status === 'in-progress' && task.status !== 'expired')
        return <FontAwesomeIcon icon={['far', 'circle-three-quarters-stroke']} color={theme.Colors.Neutrals.Darkest} />

      return <FontAwesomeIcon icon={['fal', 'clipboard-check']} color={theme.Colors.Neutrals.Darkest} />
    }
    case 'FileUploadRequest': {
      return <FontAwesomeIcon icon={['fal', 'arrow-up-from-line']} color={theme.Colors.Neutrals.Darkest} />
    }
    default: {
      return null
    }
  }
}

export const TaskItem = ({
  task,
  onClick,
  preview = false,
}: {
  task: Task
  preview?: boolean
  onClick?: () => void
}) => {
  const theme = useTheme()

  const getDescription = () => {
    switch (task.type) {
      case 'Message': {
        return i18n._(globalDefinitions.task.Message.description)
      }
      case 'Document': {
        return i18n._(globalDefinitions.task.Document.description)
      }
      case 'Questionnaire': {
        return i18n._(globalDefinitions.task.Questionnaire.description)
      }
      case 'Remecare': {
        return i18n._(globalDefinitions.task.Remecare.description)
      }
      case 'Hylyght': {
        return i18n._(globalDefinitions.task.Hylyght.description)
      }
      case 'Content': {
        return i18n._(globalDefinitions.task.Content.description)
      }
      case 'FileUploadRequest': {
        return i18n._(globalDefinitions.task.FileUploadRequest.description)
      }
    }
  }

  return (
    <FlexRow
      onClick={onClick}
      flex={1}
      gap={theme.UI.Spacing.Space4}
      justifyContent='space-between'
      alignItems='center'
      style={{
        width: '100%',
      }}
    >
      {/* Icon */}
      <FlexColumn flex={1}>
        <StTaskIcon>
          <TaskIcon task={task} />
        </StTaskIcon>
      </FlexColumn>

      {/* Display & Description */}
      <FlexColumn
        flex={1}
        gap={theme.UI.Spacing.Space1}
        justifyContent='center'
        alignItems='flex-start'
        flexGrow={1}
        style={{ overflow: 'hidden' }}
      >
        <StTaskDisplay>{task.display}</StTaskDisplay>
        <FlexRow style={{ width: '100%', overflow: 'hidden' }} gap={theme.UI.Spacing.Space2}>
          {preview ? null : <StTaskDescription>{getDescription()}</StTaskDescription>}

          <TaskStatusLabel task={task} />
        </FlexRow>
      </FlexColumn>
    </FlexRow>
  )
}

export const StTaskIcon = styled(FlexColumn)`
  border-radius: 100%;

  height: 44px;
  width: 44px;

  align-items: center;
  justify-content: center;

  border: 1px solid ${theme.Colors.Neutrals[50]};
`

const StTaskDisplay = styled.p`
  font-size: ${theme.Typography.BodySmallBold['font-size']};
  line-height: ${theme.Typography.BodySmallBold['line-height']};
  color: ${theme.Colors.Neutrals.Darkest};

  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  font-weight: 700;

  margin: 0;
  padding: 0;
`

const StTaskDescription = styled.p`
  font-size: ${theme.Typography.BodySmallRegular['font-size']};
  line-height: ${theme.Typography.BodySmallRegular['line-height']};
  color: ${theme.Colors.Neutrals.Darker};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  margin: 0;
  padding: 0;
`
